/**
 * PublicLeagueSchedulePage — /l/:slug/schedule
 * ─────────────────────────────────────────────────────────────────
 * REDESIGNED in the public pages overhaul P3 (2026-06-09). Two
 * views in one page:
 *   • Upcoming — published schedule grouped by date
 *   • Results  — final scores (scoresheet-locked games), newest first
 * Season picker; every row links to the public game page.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import {
  container, Section, Card, EmptyState,
  INK, MUTED, LINE, SURFACE, formatDateNice, formatTimeNice,
} from './pubUi.jsx';

export default function PublicLeagueSchedulePage() {
  const { league, currentSeason, accent } = useOutletContext();
  const { slug } = useParams();

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(currentSeason?.id || '');
  const [view, setView] = useState('upcoming'); // 'upcoming' | 'results'
  const [schedule, setSchedule] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => { document.title = `Schedule — ${league.name}`; }, [league.name]);

  useEffect(() => {
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons`)
      .then(({ data }) => {
        const list = data?.seasons || [];
        setSeasons(list);
        setSeasonId(prev => prev || list[0]?.id || '');
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    setSchedule(null); setResults(null);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${seasonId}/schedule`)
      .then(({ data }) => { if (!cancelled) setSchedule(data?.games || []); })
      .catch(() => { if (!cancelled) setSchedule([]); });
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${seasonId}/results`)
      .then(({ data }) => { if (!cancelled) setResults(data?.games || []); })
      .catch(() => { if (!cancelled) setResults([]); });
    return () => { cancelled = true; };
  }, [slug, seasonId]);

  // Upcoming = games not yet locked, from today on; grouped by date.
  const upcomingGroups = useMemo(() => {
    if (!schedule) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const upcoming = schedule.filter(g => !g.is_scoresheet_locked && g.game_date >= todayStr);
    const map = new Map();
    for (const g of upcoming) {
      if (!map.has(g.game_date)) map.set(g.game_date, []);
      map.get(g.game_date).push(g);
    }
    return [...map.entries()];
  }, [schedule]);

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section
        eyebrow={seasons.find(s => s.id === seasonId)?.name || ''}
        title="Schedule & results"
        action={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', background: SURFACE, borderRadius: 12, padding: 4, border: `1px solid ${LINE}` }}>
              {[['upcoming', 'Upcoming'], ['results', 'Results']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setView(v)}
                  style={{
                    padding: '8px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
                    fontWeight: 800, fontSize: 13,
                    background: view === v ? '#fff' : 'transparent',
                    color: view === v ? INK : MUTED,
                    boxShadow: view === v ? '0 1px 4px rgba(16,23,48,0.12)' : 'none',
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {seasons.length > 1 && (
              <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
                style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${LINE}`, background: '#fff', fontSize: 14, fontWeight: 600 }}>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
        }>

        {view === 'upcoming' && (
          upcomingGroups === null ? <div style={{ color: MUTED }}>Loading…</div>
          : upcomingGroups.length === 0 ? (
            <EmptyState icon="🗓">No upcoming games — check the Results tab for what's been played.</EmptyState>
          ) : (
            upcomingGroups.map(([date, games]) => (
              <div key={date} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
                  {formatDateNice(date)}
                </div>
                <Card>
                  {games.map((g, i) => (
                    <GameRow key={g.id} g={g} slug={slug} accent={accent} last={i === games.length - 1} />
                  ))}
                </Card>
              </div>
            ))
          )
        )}

        {view === 'results' && (
          results === null ? <div style={{ color: MUTED }}>Loading…</div>
          : results.length === 0 ? (
            <EmptyState icon="🥅">No final scores yet.</EmptyState>
          ) : (
            <Card>
              {results.map((g, i) => (
                <Link key={g.id} to={`/l/${slug}/game/${g.id}`} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
                  borderBottom: i < results.length - 1 ? `1px solid ${LINE}` : 'none',
                  color: INK, textDecoration: 'none',
                }}>
                  <div style={{ width: 90, flexShrink: 0, fontSize: 12, color: MUTED }}>
                    {formatDateNice(g.game_date)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: g.home_score > g.away_score ? 800 : 600 }}>{g.home_team_name}</span>
                    <span style={{
                      fontWeight: 900, fontSize: 15, padding: '2px 10px', borderRadius: 8,
                      background: SURFACE, border: `1px solid ${LINE}`,
                    }}>
                      {g.home_score} – {g.away_score}
                    </span>
                    <span style={{ fontWeight: g.away_score > g.home_score ? 800 : 600 }}>{g.away_team_name}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#10883f' }}>FINAL</span>
                  <span style={{ color: accent, fontWeight: 800 }}>→</span>
                </Link>
              ))}
            </Card>
          )
        )}
      </Section>
    </div>
  );
}

function GameRow({ g, slug, accent, last }) {
  return (
    <Link to={`/l/${slug}/game/${g.id}`} style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
      borderBottom: last ? 'none' : `1px solid ${LINE}`,
      color: INK, textDecoration: 'none',
    }}>
      <div style={{ width: 72, flexShrink: 0, fontWeight: 800, fontSize: 14 }}>
        {formatTimeNice(g.game_time)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>
          {g.home_team_name} <span style={{ color: MUTED, fontWeight: 500 }}>vs</span> {g.away_team_name}
        </div>
        {g.venue_name && <div style={{ fontSize: 12, color: MUTED }}>{g.venue_name}</div>}
      </div>
      <span style={{ color: accent, fontWeight: 800 }}>→</span>
    </Link>
  );
}
